/**
 * Page d'une section de la console : titre, onglets (et sous-onglets) ouverts
 * au compte, puis l'écran demandé. L'écran est fourni par la table des routes
 * (`routes/routesDesSections`) ; cette page ne fait que l'encadrer et
 * retrouver, depuis l'adresse, quel onglet est actif.
 */
import React, { Suspense } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useRBAC } from '../../contexts/RBACContext';
import type { Section } from '../../config/sections';
import OngletsNiveau from './OngletsNiveau';
import { cheminComplet, ongletActif, ongletsVisibles } from './ongletsVisibles';

interface SectionPageProps {
  section: Section;
  /** Écran de l'onglet (ou de la fiche de détail) demandé. */
  ecran: React.ReactElement;
}

/** Attente du code de l'écran, chargé à l'ouverture de son onglet. */
const ChargementEcran: React.FC = () => (
  <Box display="flex" justifyContent="center" py={6}><CircularProgress aria-label="Chargement de l'écran" /></Box>
);

/** Section : en-tête, deux niveaux d'onglets au plus, écran. */
const SectionPage: React.FC<SectionPageProps> = ({ section, ecran }) => {
  const { hasRole } = useRBAC();
  const { pathname } = useLocation();
  const onglets = ongletsVisibles(section.onglets, hasRole);
  const actif = ongletActif(onglets, section.chemin, pathname);
  const baseSousOnglets = actif ? cheminComplet(section.chemin, actif.chemin) : section.chemin;
  const sousOnglets = actif?.onglets ? ongletsVisibles(actif.onglets, hasRole) : [];
  const sousActif = ongletActif(sousOnglets, baseSousOnglets, pathname);

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 1, fontWeight: 700 }}>
        {section.titre}
      </Typography>
      <OngletsNiveau onglets={onglets} base={section.chemin} actif={actif} libelle={section.titre} />
      {actif?.onglets && (
        <OngletsNiveau onglets={sousOnglets} base={baseSousOnglets} actif={sousActif} libelle={actif.titre} secondaire />
      )}
      <Suspense fallback={<ChargementEcran />}>{ecran}</Suspense>
    </Box>
  );
};

export default SectionPage;
